import type { PropsWithChildren } from 'react';
import { createContext, useEffect, useMemo, useReducer } from 'react';
import { useSearchParams } from 'react-router';

import { Storage } from '@jane/shared/util';

import { idReducer, parseIdArray } from '../../util';
import { useEnsureContextWithinProvider } from './hooks/useEnsureContextWithinProvider';

interface ProductTrackingContext {
  cartToppers: {
    addCartTopperId: (productId: number) => void;
    cartTopperIds: number[];
    removeCartTopperId: (productId: number) => void;
    resetCartTopperIds: () => void;
  };
  inlineAds: {
    addInlineAdId: (productId: number) => void;
    inlineAdIds: number[];
    removeInlineAdId: (productId: number) => void;
    resetInlineAdIds: () => void;
  };
}

const ProductTrackingContext = createContext<ProductTrackingContext | null>(
  null
);

const idsFromQueryOrStorage = (
  key: string,
  queryParams: URLSearchParams
): number[] => {
  const queryIds = parseIdArray(queryParams.get(key));

  if (queryIds.length !== 0) {
    return queryIds;
  }

  const storageIds = Storage.get(key);
  if (storageIds && storageIds.length !== 0) {
    return storageIds;
  }

  return [];
};

export const ProductTrackingProvider = ({ children }: PropsWithChildren) => {
  const [queryParams] = useSearchParams();

  const [cartTopperIds, dispatchTopperAction] = useReducer(
    idReducer,
    idsFromQueryOrStorage('topperIds', queryParams)
  );
  const [inlineAdIds, dispatchInlineAction] = useReducer(
    idReducer,
    idsFromQueryOrStorage('inlineIds', queryParams)
  );

  const completeContext = useMemo(() => {
    return {
      cartToppers: {
        cartTopperIds,
        addCartTopperId: (productId: number) =>
          dispatchTopperAction({ type: 'add', productId }),
        removeCartTopperId: (productId: number) =>
          dispatchTopperAction({ type: 'remove', productId }),
        resetCartTopperIds: () => dispatchTopperAction({ type: 'reset' }),
      },
      inlineAds: {
        inlineAdIds,
        addInlineAdId: (productId: number) =>
          dispatchInlineAction({ type: 'add', productId }),
        removeInlineAdId: (productId: number) =>
          dispatchInlineAction({ type: 'remove', productId }),
        resetInlineAdIds: () => dispatchInlineAction({ type: 'reset' }),
      },
    };
  }, [cartTopperIds, inlineAdIds]);

  useEffect(() => {
    Storage.set('topperIds', cartTopperIds);
    Storage.set('inlineIds', inlineAdIds);
  }, [cartTopperIds, inlineAdIds]);

  return (
    <ProductTrackingContext.Provider value={completeContext}>
      {children}
    </ProductTrackingContext.Provider>
  );
};

export const useProductTracking = () =>
  useEnsureContextWithinProvider(ProductTrackingContext, 'ProductTracking');
